import React, { useEffect, useMemo, useState } from 'react';
import { Personnel } from '../../../../shared/types/game';
import { PersonnelConfig } from '../../../../shared/types/config';
import { getApiBaseUrl } from '../../../../core/config/api';
import { getServiceIdFromBuildingType } from '../../utils/buildingServiceMapping';
import { usePersonnelTab } from './usePersonnelTab';

/**
 * Onglet « Personnel » — port fidèle du prototype (`buildings.js` tabPersonnel /
 * ficheAgent) : bloc « Effectif » (recrutement inline + recherche + tableau) et
 * bloc « Dossier agent » (grade, statut, groupe, socle/spécialités, bonus,
 * planning, radiation). Visuel .oscockpit, branché sur les vraies données.
 */

interface BuildingPersonnelTabProps {
  personnel: Personnel[];
  loadingPersonnel: boolean;
  buildingType: string;
  buildingId: number;
  buildingName?: string;
  userId: number;
  buildingCapacity?: number;
  onPersonnelAdded?: () => void;
}

const BuildingPersonnelTab: React.FC<BuildingPersonnelTabProps> = ({
  personnel,
  loadingPersonnel,
  buildingType,
  buildingId,
  buildingCapacity = 0,
  onPersonnelAdded,
}) => {
  const {
    isRecruiting,
    recruitError,
    recruitSuccess,
    handleRecruit,
    filteredPersonnel,
    paginatedPersonnel,
    totalPages,
    currentPage,
    setCurrentPage,
    personnelSearchQuery,
    setPersonnelSearchQuery,
    personnelFilterActif,
    setPersonnelFilterActif,
    selectedPersonnel,
    personnelCompetences,
    gradeDefaultCompetences,
    loadingCompetences,
    personnelGroupeSelect,
    savingGroupe,
    deletingPersonnelId,
    handleOpenPersonnelDetails,
    handleDeletePersonnel,
    savePersonnelGroupe,
    getStatutGeneralColors,
    getStatutPlanningColors,
  } = usePersonnelTab({ personnel, buildingType, buildingId, onPersonnelAdded });

  // ── Recrutement inline (grade + quantité) ──
  const serviceId = getServiceIdFromBuildingType(buildingType);
  const [configs, setConfigs] = useState<PersonnelConfig[]>([]);
  const [recruitGrade, setRecruitGrade] = useState<string>('');
  const [recruitQty, setRecruitQty] = useState<number>(1);

  useEffect(() => {
    let alive = true;
    (async () => {
      const token = localStorage.getItem('token');
      if (!token || !serviceId) return;
      try {
        const res = await fetch(`${getApiBaseUrl(true)}/config/personnel`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json().catch(() => null);
        if (!alive || !data?.success || !Array.isArray(data.data)) return;
        const list = (data.data as PersonnelConfig[]).filter((c) => c.id_service === serviceId);
        setConfigs(list);
        if (list.length) setRecruitGrade(String(list[0].id));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, [serviceId]);

  const availableSlots = buildingCapacity > 0 ? Math.max(0, buildingCapacity - personnel.length) : 99;

  const doRecruit = () => {
    const cfg = configs.find((c) => String(c.id) === recruitGrade);
    if (cfg) handleRecruit(cfg, recruitQty);
  };

  // ── Dossier agent : socle vs spécialités ──
  const specialites = useMemo(
    () => personnelCompetences.filter((c) => !gradeDefaultCompetences.some((g) => g.id === c.id)),
    [personnelCompetences, gradeDefaultCompetences],
  );

  const armablesCount = personnel.filter((p) => p.actif).length;

  const ficheAgent = (p: Personnel) => (
    <>
      <div className="stats4">
        <div className="stat"><b>{(p as unknown as { interventions_count?: number }).interventions_count ?? 0}</b><span>interventions</span></div>
        <div className="stat"><b>{p.age ?? '—'}</b><span>ans</span></div>
        <div className="stat"><b>{p.age_retraite ?? '—'}</b><span>retraite</span></div>
        <div className="stat"><b>GR-{p.groupe || '—'}</b><span>groupe</span></div>
      </div>
      <dl className="kv" style={{ marginTop: 8 }}>
        <dt>Grade</dt>
        <dd>{p.grade_code || p.grade || '—'}</dd>
        <dt>Statut</dt>
        <dd>
          {(() => {
            const c = getStatutGeneralColors(p.statut_nom ?? null);
            return <span className="pbadge" style={{ background: c.backgroundColor, color: c.textColor }}>{p.statut_nom || (p.actif ? 'Disponible' : 'Indisponible')}</span>;
          })()}
        </dd>
        <dt>Groupe</dt>
        <dd>
          <select
            value={personnelGroupeSelect}
            disabled={savingGroupe}
            onChange={(e) => savePersonnelGroupe(e.target.value as '' | 'A' | 'B' | 'C' | 'D')}
            className="minisel"
          >
            <option value="">— aucun —</option>
            {['A', 'B', 'C', 'D'].map((g) => (
              <option key={g} value={g}>GR-{g}</option>
            ))}
          </select>
        </dd>
      </dl>

      <p className="bsec">Socle du grade <span className="muted">conféré à l'embauche</span></p>
      <div className="chips">
        {loadingCompetences ? (
          <span className="muted">Chargement…</span>
        ) : gradeDefaultCompetences.length ? (
          gradeDefaultCompetences.map((c) => <span className="chip2" key={c.id}>{c.nom}</span>)
        ) : (
          <span className="muted">Aucune</span>
        )}
      </div>

      <p className="bsec">Spécialités <span className="muted">acquises en formation</span></p>
      <div className="chips">
        {specialites.length ? (
          specialites.map((c) => <span className="chip2" key={c.id}>{c.nom}</span>)
        ) : (
          <span className="muted">Aucune</span>
        )}
      </div>

      <p className="bsec">Bonus <span className="muted">tirés à l'embauche</span></p>
      <div className="bonuslist">
        {(p.bonus || []).length ? (
          (p.bonus || []).map((x, i) => (
            <div className={`bline ${x.premium ? 'is-prem' : ''}`} key={i}>
              <span className="bline__ico">•</span>
              <span className="bline__nom">{x.type}</span>
              <span className="bline__val mono">+{x.valeur}</span>
            </div>
          ))
        ) : (
          <span className="muted">Aucun bonus</span>
        )}
      </div>

      <div className="brecrute" style={{ marginTop: 9 }}>
        <span className="bsub" style={{ flex: 1 }}>Planning : onglet <b>Planning › Grille du personnel</b>.</span>
        <button
          className="btn btn--sm btn--danger"
          type="button"
          disabled={deletingPersonnelId === p.id}
          onClick={() => handleDeletePersonnel(p)}
        >
          {deletingPersonnelId === p.id ? 'Radiation…' : 'Rayer'}
        </button>
      </div>
    </>
  );

  return (
    <div className="bsplit">
      {/* ── Effectif ── */}
      <div className="block">
        <div className="block__h">
          Effectif <span className="mono muted">{filteredPersonnel.length}{filteredPersonnel.length !== personnel.length ? ` / ${personnel.length}` : ''} · {armablesCount} armable(s)</span>
        </div>
        <div className="block__b">
          <div className="brecrute">
            <select value={recruitGrade} onChange={(e) => setRecruitGrade(e.target.value)}>
              {configs.length ? (
                configs.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {(c.grade_code || c.grade)} · {c.prix.toLocaleString('fr-FR')} €
                  </option>
                ))
              ) : (
                <option value="">Aucun grade disponible</option>
              )}
            </select>
            <select value={recruitQty} onChange={(e) => setRecruitQty(Number(e.target.value))} style={{ flex: '0 0 62px' }}>
              {[1, 2, 5, 10].map((n) => (
                <option key={n} value={n}>×{n}</option>
              ))}
            </select>
            <button className="btn btn--sm btn--primary" type="button" disabled={!configs.length || isRecruiting || availableSlots <= 0} onClick={doRecruit}>
              {isRecruiting ? 'Recrutement…' : 'Recruter'}
            </button>
          </div>
          {recruitError ? <p className="bwarn">⚠ {recruitError}</p> : null}
          {recruitSuccess ? <p className="bsub" style={{ color: '#6ee7b7' }}>{recruitSuccess}</p> : null}
          <p className="bsub">Le recrutement confère le socle de compétences du grade — seules les spécialités passent par une formation.</p>

          <div className="toolbar">
            <input type="search" placeholder="Nom, prénom, grade…" value={personnelSearchQuery} onChange={(e) => setPersonnelSearchQuery(e.target.value)} autoComplete="off" />
            <select value={personnelFilterActif} onChange={(e) => setPersonnelFilterActif(e.target.value)}>
              <option value="all">Tous</option>
              <option value="actif">Actifs</option>
              <option value="inactif">Inactifs</option>
            </select>
          </div>

          {loadingPersonnel ? (
            <div className="empty">Chargement du personnel…</div>
          ) : filteredPersonnel.length === 0 ? (
            <div className="empty">Aucun agent ne correspond au filtre.</div>
          ) : (
            <table className="table btable">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Grade</th>
                  <th>GR</th>
                  <th>Planning du jour</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPersonnel.map((p) => {
                  const planNom = p.planning_statut_nom;
                  const planCol = planNom ? getStatutPlanningColors(planNom) : null;
                  const statNom = p.statut_nom || (p.actif ? 'Disponible' : 'Indisponible');
                  const statCol = getStatutGeneralColors(p.statut_nom ?? null);
                  return (
                    <tr
                      key={p.id}
                      className={selectedPersonnel?.id === p.id ? 'is-sel' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleOpenPersonnelDetails(p)}
                    >
                      <td>{`${p.prenom || ''} ${p.nom || ''}`.trim() || `Agent ${p.id}`}{p.premium ? ' ★' : ''}</td>
                      <td className="mono">{p.grade_code || p.grade || '—'}</td>
                      <td className="mono muted">{p.groupe || '—'}</td>
                      <td>
                        {planNom && planCol ? (
                          <span className="pbadge" style={{ background: planCol.backgroundColor, color: planCol.textColor }}>{planNom}</span>
                        ) : '—'}
                      </td>
                      <td>
                        <span className="pbadge" style={{ background: statCol.backgroundColor, color: statCol.textColor }}>{statNom}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {totalPages > 1 ? (
            <div className="brecrute" style={{ marginTop: 8, alignItems: 'center' }}>
              <span className="bsub" style={{ flex: 1 }}>Page {currentPage} / {totalPages}</span>
              <button className="btn btn--sm btn--ghost" type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Précédent</button>
              <button className="btn btn--sm btn--ghost" type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Suivant</button>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Dossier agent ── */}
      <div className="block">
        <div className="block__h">{selectedPersonnel ? `Dossier agent — ${selectedPersonnel.prenom} ${selectedPersonnel.nom}` : 'Dossier agent'}</div>
        <div className="block__b">
          {selectedPersonnel ? ficheAgent(selectedPersonnel) : <div className="empty">Sélectionnez un agent dans la liste.</div>}
        </div>
      </div>
    </div>
  );
};

export default BuildingPersonnelTab;
